import type { BuildingGeometryEmitter } from "building-types/geometry/buildingGeometryTypes";
import type { DocEntityHandle } from "building-types/handles/docEntityHandleTypes";
import type { ExteriorPalette } from "building-types/materials/exteriorPaletteTypes";
import { FaceCategory } from "building-types/materials/faceCategories";
import type { WainscotingSettings } from "building-types/settings/wainscotingSettings";
import type { Wainscoting } from "building-types/wainscoting/wainscotingTypes";
import type { Table } from "common/data/table";
import { docEntityHandleHash } from "doc/entity/core/handleHash";
import { materialSrcFromPalette } from "doc/materialLookup";
import { faceGroupNew, type Face } from "geom/brep";
import { UVGeneration } from "geom/uv";
import { buildExteriorLayers } from "./buildingGeometryTools";

interface HasWainscotingSettings {
  wainscoting: WainscotingSettings;
}

const MIN_WAINSCOTING_THICKNESS = 0.25;

interface BuildingForWainscotingGeometry<T extends HasWainscotingSettings> {
  wainscoting: Table<Wainscoting>;
  settings: T;
  exteriorPalette: ExteriorPalette;
}

function iterateWainscoting<T extends HasWainscotingSettings>(
  building: BuildingForWainscotingGeometry<T>,
  callback: (wainscoting: Wainscoting) => void,
) {
  for (const wainsId in building.wainscoting) {
    const wainscoting = building.wainscoting[wainsId];

    if (wainscoting.thickness <= MIN_WAINSCOTING_THICKNESS) {
      continue;
    }

    callback(wainscoting);
  }
}

/** Faces of one wainscoting wall segment, which share a pick handle and a reporting unit. */
interface WainscotingFaceGroup {
  readonly pickHandle: DocEntityHandle | undefined;
  readonly reportingUnitHandle: DocEntityHandle | undefined;
  readonly faces: Face[];
}

export function getWainscotingGeometry<T extends HasWainscotingSettings>(
  building: BuildingForWainscotingGeometry<T>,
  emitter: BuildingGeometryEmitter,
): void {
  const {
    settings: { wainscoting: wainscotingSettings },
    exteriorPalette,
  } = building;
  const { material = "masonry0" } = wainscotingSettings;

  iterateWainscoting(building, (wainscoting) => {
    const { levelId, faces, wainscotingDescriptor, facePickHandles, faceReportingUnitHandles } = wainscoting;

    const wainscotingMaterial = materialSrcFromPalette(exteriorPalette, material);

    if (facePickHandles !== undefined && facePickHandles.length !== faces.length) {
      throw new Error(
        `Wainscoting facePickHandles length mismatch: expected ${faces.length}, received ${facePickHandles.length}`,
      );
    }

    // Grouped by hash, since handles aren't value-comparable; "" is the no-handle key, so those
    // faces share the same grouping + emit flow instead of a second code path.
    // Faces sharing a pick handle come from one wall segment, so they share a reporting-unit handle.
    const groups = new Map<string, WainscotingFaceGroup>();

    if (facePickHandles !== undefined) {
      for (let i = 0; i < faces.length; i++) {
        const pickHandle = facePickHandles[i];
        const key = pickHandle ? docEntityHandleHash(pickHandle) : "";
        let group = groups.get(key);
        if (!group) {
          group = { pickHandle, reportingUnitHandle: faceReportingUnitHandles?.[i], faces: [] };
          groups.set(key, group);
        }
        group.faces.push(faces[i]);
      }
    } else {
      groups.set("", { pickHandle: undefined, reportingUnitHandle: undefined, faces });
    }

    for (const { pickHandle, reportingUnitHandle, faces: groupFaces } of groups.values()) {
      emitter.emit({
        levelId,
        geom: faceGroupNew(groupFaces),
        materialSlot: material,
        materialSrc: wainscotingMaterial,
        uvGen: UVGeneration.NONE,
        threeLayers: buildExteriorLayers(),
        entityId: wainscotingDescriptor.entityId,
        faceCategory: FaceCategory.WAINSCOTING,
        descriptor: wainscotingDescriptor,
        pickHandle,
        entityHandleForSelectingReportingUnit: reportingUnitHandle,
      });
    }
  });
}

export function getWainscotingTrimGeometry<T extends HasWainscotingSettings>(
  building: BuildingForWainscotingGeometry<T>,
  emitter: BuildingGeometryEmitter,
): void {
  const {
    settings: { wainscoting: wainscotingSettings },
    exteriorPalette,
  } = building;
  const { trimMaterial = "masonryTrim0:Rowlock" } = wainscotingSettings;

  iterateWainscoting(building, (wainscoting) => {
    const { levelId, trims, capDescriptor, trimPickHandles, trimReportingUnitHandles } = wainscoting;

    const wainscotingTrimMaterial = materialSrcFromPalette(exteriorPalette, trimMaterial);

    for (let i = 0; i < trims.length; i++) {
      const pickHandle = trimPickHandles?.[i];
      emitter.emit({
        levelId,
        geom: trims[i],
        materialSlot: trimMaterial,
        materialSrc: wainscotingTrimMaterial,
        uvGen: UVGeneration.GLOBAL_RECT_UV,
        threeLayers: buildExteriorLayers(),
        faceCategory: FaceCategory.WAINSCOTING_TRIM,
        entityId: capDescriptor.entityId,
        descriptor: capDescriptor,
        pickHandle,
        entityHandleForSelectingReportingUnit: trimReportingUnitHandles?.[i],
      });
    }
  });
}
